import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@finitless/shared/components";
import { useBusinesses } from "@finitless/shared/hooks";
import {
  useLinkInBioDelete,
  useLinkInBioFranchiseList,
  useLinkInBioSetEnabled,
} from "../../../hooks/useLinkInBioAdmin";
import { getLinkInBioAdminFunctionUrl } from "../../../lib/linkInBioAdminApi";
import { getSupabasePublicClient } from "../../../lib/supabasePublicClient";
import { Button, Icon, Switch, buttonVariants } from "../../ui";
import DeleteConfirmationModal from "../../ui/Modals/DeleteConfirmationModal";
import { LinkInBioListRowsSkeleton } from "./LinkInBioSkeletons";
import { cn } from "@/lib/utils";
import { useToast } from "@finitless/shared/hooks";
import { useApiStore } from "@finitless/shared/stores";
import {
  buildPublicLinkInBioUrl,
  getLinkInBioPublicBaseUrl,
} from "../../../lib/linkInBio/linkInBioPublicPath";

/** Dashboard card title: display_name, then slug. */
function primaryListTitle(row) {
  const displayName =
    row.display_name != null ? String(row.display_name).trim() : "";
  if (displayName) return displayName;
  return String(row.slug || "");
}

function getPublicPageUrl(slug, finitlessProject) {
  if (typeof window === "undefined" || slug == null || slug === "") {
    return "";
  }
  const p = finitlessProject != null ? String(finitlessProject).trim() : "";
  if (!p) {
    return "";
  }
  return buildPublicLinkInBioUrl(getLinkInBioPublicBaseUrl(), p, String(slug));
}

function businessLabelForRow(row, businesses) {
  const bid = row?.business_id != null ? String(row.business_id).trim() : "";
  if (!bid) {
    return "";
  }
  const hit = businesses.find((b) => String(b.id) === bid);
  const name = hit?.name != null ? String(hit.name).trim() : "";
  return name || bid;
}

function editPathForRow(row) {
  return `/growth/link-in-bio/${row.id}`;
}

const LinkInBioListPage = () => {
  const { t } = useTranslation("linkInBio");
  const toast = useToast();
  const apiProject = useApiStore((s) => String(s.project || "").trim());

  const supabaseReady = Boolean(getSupabasePublicClient());
  const functionReady = Boolean(getLinkInBioAdminFunctionUrl());

  const { data: businessesData, isLoading: businessesLoading } = useBusinesses({
    enabledFilter: "enabled",
    searchTerm: "",
    includePagination: false,
  });
  const businessesList = businessesData?.businesses ?? [];

  const {
    data: rows = [],
    isLoading: listLoading,
    isError,
    refetch,
  } = useLinkInBioFranchiseList({
    enabled: supabaseReady && functionReady,
  });

  const deleteMutation = useLinkInBioDelete();
  const setEnabledMutation = useLinkInBioSetEnabled();

  const [deleteTarget, setDeleteTarget] = useState(null);

  const isLoading = listLoading;

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id || !deleteTarget?.business_id) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({
        profileId: deleteTarget.id,
        business_id: deleteTarget.business_id,
      });
      toast.success(t("list.deleteSuccess"));
      setDeleteTarget(null);
    } catch {
      toast.error(t("list.deleteError"));
    }
  };

  const handleEnabledChange = (row, enabled) => {
    const bid = row?.business_id != null ? String(row.business_id).trim() : "";
    if (!bid) {
      return;
    }
    setEnabledMutation.mutate(
      { profileId: row.id, enabled, business_id: bid },
      {
        onError: () => toast.error(t("list.toggleEnabledError")),
      },
    );
  };

  const isTogglePending = (row) =>
    setEnabledMutation.isPending &&
    String(setEnabledMutation.variables?.profileId) === String(row.id) &&
    String(setEnabledMutation.variables?.business_id) ===
      String(row.business_id);

  const isDeletePendingForRow = (row) =>
    deleteMutation.isPending &&
    String(deleteMutation.variables?.profileId) === String(row.id) &&
    String(deleteMutation.variables?.business_id) === String(row.business_id);

  const listHeaderBlock = (
    <div className="min-w-0 flex-1">
      <PageHeader title={t("list.title")} subtitle={t("list.subtitle")} />
    </div>
  );

  if (!supabaseReady || !functionReady) {
    return (
      <div className="space-y-6">
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <div className="border-destructive/30 bg-destructive/5 text-destructive rounded-xl border p-4 text-sm">
          <p className="font-semibold">{t("configBanner.title")}</p>
          <p className="text-destructive/90 mt-2">{t("configBanner.body")}</p>
        </div>
      </div>
    );
  }

  if (!businessesLoading && businessesList.length === 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        {t("noBusiness")}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          {listHeaderBlock}
        </div>
        <LinkInBioListRowsSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          {listHeaderBlock}
        </div>
        <div className="border-border rounded-xl border p-6 text-center">
          <p className="text-destructive text-sm">{t("list.loadError")}</p>
          <Button type="button" className="mt-4" onClick={() => refetch()}>
            {t("actions.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center sm:items-start">
        {listHeaderBlock}
        {rows.length > 0 ? (
          <Link
            to="/growth/link-in-bio/new"
            className={cn(
              buttonVariants({ variant: "default" }),
              "inline-flex shrink-0 gap-2 self-start sm:self-auto",
            )}
          >
            <Icon name="Plus" className="h-4 w-4" />
            {t("list.create")}
          </Link>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="border-border bg-card text-card-foreground rounded-xl border p-10 text-center shadow-sm">
          <p className="text-muted-foreground text-sm">{t("list.empty")}</p>
          <Link
            to="/growth/link-in-bio/new"
            className={cn(
              buttonVariants({ variant: "default" }),
              "mt-4 inline-flex",
            )}
          >
            {t("list.createFirst")}
          </Link>
        </div>
      ) : (
        <ul
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          role="list"
        >
          {rows.map((row) => {
            const title = primaryListTitle(row);
            const rowProject =
              row.finitless_project != null &&
              String(row.finitless_project).trim() !== ""
                ? String(row.finitless_project).trim()
                : apiProject;
            const publicPageUrl = getPublicPageUrl(row.slug, rowProject);
            const pathLabel = t("list.pathPreview", {
              project: rowProject || "…",
              slug: row.slug,
            });
            const canOpenPublic =
              Boolean(row.enabled) && Boolean(publicPageUrl);
            const storeLabel = businessLabelForRow(row, businessesList);

            return (
              <li key={row.id} className="flex h-full min-h-0">
                <article
                  className={cn(
                    "border-border bg-card text-card-foreground flex h-full min-h-0 w-full flex-col rounded-xl border p-5 shadow-sm",
                    "transition-[box-shadow,border-color] duration-200",
                    "hover:border-primary/25 hover:shadow-md",
                  )}
                >
                  <div className="min-w-0 flex-1 space-y-2">
                    {storeLabel ? (
                      <p className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                        <span
                          className={cn(
                            "bg-muted/80 text-muted-foreground border-border/70 inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-medium",
                            "ring-primary/10 ring-1",
                          )}
                        >
                          <Icon
                            name="Store"
                            className="h-3 w-3 shrink-0 opacity-70"
                            aria-hidden
                          />
                          <span className="truncate">{storeLabel}</span>
                        </span>
                      </p>
                    ) : null}
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <h3 className="text-foreground min-w-0 flex-1 text-base leading-snug font-semibold">
                        <span className="line-clamp-3 break-words">
                          {title}
                        </span>
                      </h3>
                      <Switch
                        id={`lib-list-enabled-${row.id}`}
                        checked={Boolean(row.enabled)}
                        onCheckedChange={(next) =>
                          handleEnabledChange(row, next)
                        }
                        disabled={isTogglePending(row)}
                        aria-label={t("list.enableSwitchAria")}
                        className="mt-0.5 shrink-0"
                      />
                    </div>
                    <div className="pt-1">
                      {canOpenPublic ? (
                        <a
                          href={publicPageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/90 inline-flex max-w-full items-start gap-1.5 font-mono text-sm break-words underline-offset-4 transition-colors hover:underline"
                        >
                          <Icon
                            name="Link"
                            className="text-primary mt-0.5 h-3.5 w-3.5 shrink-0"
                            aria-hidden
                          />
                          <span className="min-w-0">{pathLabel}</span>
                        </a>
                      ) : (
                        <span
                          className="text-muted-foreground inline-flex max-w-full cursor-not-allowed items-start gap-1.5 font-mono text-sm break-words"
                          title={t("list.publicLinkDisabledHint")}
                        >
                          <Icon
                            name="Link"
                            className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60"
                            aria-hidden
                          />
                          <span className="min-w-0">{pathLabel}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="border-border mt-5 grid grid-cols-2 gap-2 border-t pt-5">
                    <Link
                      to={editPathForRow(row)}
                      className={cn(
                        buttonVariants({ variant: "default", size: "sm" }),
                        "inline-flex min-h-9 w-full items-center justify-center px-2",
                      )}
                    >
                      {t("list.edit")}
                    </Link>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-destructive border-destructive/45 hover:bg-destructive/10 hover:text-destructive min-h-9 w-full px-2"
                      disabled={isDeletePendingForRow(row)}
                      onClick={() =>
                        setDeleteTarget({
                          id: row.id,
                          label: primaryListTitle(row),
                          business_id: String(row.business_id || "").trim(),
                        })
                      }
                    >
                      {t("list.remove")}
                    </Button>
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        itemName={deleteTarget?.label}
        itemType={t("list.deleteModal.itemType")}
        title={t("list.deleteModal.title")}
        subtitle={t("list.deleteModal.subtitle")}
        confirmButtonText={t("list.deleteModal.confirm")}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default LinkInBioListPage;
